import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Button,
  message,
  Row,
  Col,
  Card,
  Checkbox,
  Space,
  Divider,
  Tag,
  Typography,
} from "antd";
import { 
  SearchOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  PlusOutlined,
  DeleteOutlined 
} from "@ant-design/icons";

const { Option } = Select;
const { Text, Title } = Typography;
const { Search } = Input;

const DataSelectorModal = ({
  visible,
  onCancel,
  onOk,
  dataType,
  selectedData = [],
  availableData = [],
  loading = false,
  title = "Select Data",
}) => {
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(availableData);
  const [localSelectedData, setLocalSelectedData] = useState(selectedData);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    setLocalSelectedData(selectedData);
  }, [selectedData]);

  useEffect(() => {
    setFilteredData(availableData);
  }, [availableData]);

  // Filter data based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = availableData.filter(item =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.label?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(availableData);
    }
  }, [searchTerm, availableData]);

  // Handle individual item selection
  const handleItemSelect = (item, checked) => {
    if (checked) {
      setLocalSelectedData(prev => [...prev, item]);
    } else {
      setLocalSelectedData(prev => prev.filter(selected => selected.id !== item.id));
    }
  };

  // Handle select all toggle
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setLocalSelectedData(filteredData);
    } else {
      setLocalSelectedData([]);
    }
  };

  // Check if item is selected
  const isItemSelected = (item) => {
    return localSelectedData.some(selected => selected.id === item.id);
  };

  // Handle OK button
  const handleOk = () => {
    onOk(localSelectedData);
  };

  // Handle cancel
  const handleCancel = () => {
    setLocalSelectedData(selectedData); // Reset to original selection
    setSearchTerm("");
    onCancel();
  };

  // Remove selected item
  const removeSelectedItem = (itemId) => {
    setLocalSelectedData(prev => prev.filter(item => item.id !== itemId));
  };

  // Get display name for item
  const getDisplayName = (item) => {
    return item.name || item.title || item.label || item.value || item.id;
  };

  // Get data type label
  const getDataTypeLabel = () => {
    switch (dataType) {
      case 'copilotTypes':
        return 'AI Copilot Types';
      case 'industries':
        return 'Industries';
      case 'professionalTasks':
        return 'Professional Tasks';
      case 'locations':
        return 'Locations';
      case 'competitors':
        return 'Competitors';
      default:
        return 'Data Items';
    }
  };

  return (
    <Modal
      title={title || `Select ${getDataTypeLabel()}`}
      visible={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      width={800}
      okText="Apply Selection"
      cancelText="Cancel"
      confirmLoading={loading}
      bodyStyle={{ padding: '20px' }}
    >
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={16}>
            <Search
              placeholder={`Search ${getDataTypeLabel().toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col span={8}>
            <Checkbox
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              Select All ({filteredData.length})
            </Checkbox>
          </Col>
        </Row>
      </div>

      {/* Selected Items Summary */}
      {localSelectedData.length > 0 && (
        <Card 
          size="small" 
          title={`Selected Items (${localSelectedData.length})`}
          style={{ marginBottom: 16 }}
        >
          <div style={{ maxHeight: 100, overflowY: 'auto' }}>
            <Space size={[0, 8]} wrap>
              {localSelectedData.map((item) => (
                <Tag
                  key={item.id}
                  closable
                  onClose={() => removeSelectedItem(item.id)}
                  color="blue"
                >
                  {getDisplayName(item)}
                </Tag>
              ))}
            </Space>
          </div>
        </Card>
      )}

      <Divider />

      {/* Available Items List */}
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {filteredData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            <Text type="secondary">
              {searchTerm ? 'No items found matching your search.' : 'No data available.'}
            </Text>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {filteredData.map((item) => {
              const isSelected = isItemSelected(item);
              return (
                <Col span={24} key={item.id}>
                  <Card 
                    size="small" 
                    hoverable
                    style={{
                      border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      backgroundColor: isSelected ? '#f6ffed' : '#fff'
                    }}
                  >
                    <Row align="middle" justify="space-between">
                      <Col span={20}>
                        <Space>
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => handleItemSelect(item, e.target.checked)}
                          />
                          <div>
                            <Text strong>{getDisplayName(item)}</Text>
                            {item.description && (
                              <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {item.description}
                                </Text>
                              </div>
                            )}
                            {item.category && (
                              <div>
                                <Tag size="small" color="default">
                                  {item.category}
                                </Tag>
                              </div>
                            )}
                          </div>
                        </Space>
                      </Col>
                      <Col span={4} style={{ textAlign: 'right' }}>
                        {isSelected ? (
                          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                        ) : (
                          <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: '16px' }} />
                        )}
                      </Col>
                    </Row>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* Footer Statistics */}
      <Divider />
      <Row justify="space-between" align="middle">
        <Col>
          <Text type="secondary">
            Showing {filteredData.length} of {availableData.length} items
          </Text>
        </Col>
        <Col>
          <Text type="secondary">
            {localSelectedData.length} selected
          </Text>
        </Col>
      </Row>
    </Modal>
  );
};

export default DataSelectorModal;